import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  alterarStatusOrigemCarregamentoTrilhosNovos,
  cadastrarOrigemCarregamentoTrilhosNovos,
  editarOrigemCarregamentoTrilhosNovos,
  excluirOrigemCarregamentoTrilhosNovos,
  listarOrigensCarregamentoTrilhosNovos,
} from "./origemCarregamentoTrilhosNovos.api";
import CriarOrigemCarregamentoTrilhosNovosModal from "./popups/CriarOrigemCarregamentoTrilhosNovosModal";
import EditarOrigemCarregamentoTrilhosNovosModal from "./popups/EditarOrigemCarregamentoTrilhosNovosModal";
import ExcluirOrigemCarregamentoTrilhosNovosModal from "./popups/ExcluirOrigemCarregamentoTrilhosNovosModal";
import AtivarOrigemCarregamentoTrilhosNovosModal from "./popups/AtivarOrigemCarregamentoTrilhosNovosModal";
import InativarOrigemCarregamentoTrilhosNovosModal from "./popups/InativarOrigemCarregamentoTrilhosNovosModal";
import { useInfiniteBaseList } from "../shared/useInfiniteBaseList";
import "./OrigemCarregamentoTrilhosNovosBase.css";

const safeText = (value) => String(value ?? "").trim();
const formatOrigem = (row) => `${safeText(row?.cidade)} - ${safeText(row?.uf)}`;

const sanitizeCidade = (value, max = 80) =>
  String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/[<>]/g, "")
    .trim()
    .toUpperCase()
    .slice(0, max);

const sanitizeUf = (value) =>
  String(value ?? "")
    .replace(/[^A-Za-z]/g, "")
    .trim()
    .toUpperCase()
    .slice(0, 2);

function IconEdit(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function ToggleSwitch({ on = false, onToggle, title }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={`ocn-toggle ${on ? "on" : "off"}`}
      onClick={onToggle}
      title={title || (on ? "Inativar" : "Ativar")}
    >
      <span className="ocn-toggle-knob" />
    </button>
  );
}

export default function OrigemCarregamentoTrilhosNovosBase() {
  const [feedback, setFeedback] = useState("");

  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");

  const [openCriar, setOpenCriar] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [openExcluir, setOpenExcluir] = useState(false);
  const [openAtivar, setOpenAtivar] = useState(false);
  const [openInativar, setOpenInativar] = useState(false);
  const [rowSelecionada, setRowSelecionada] = useState(null);
  const tableScrollRef = useRef(null);

  const { rows, loading, loadingMore, hasMore, error, reload, loadMore } = useInfiniteBaseList({
    listFn: listarOrigensCarregamentoTrilhosNovos,
    queryText: filtroTexto,
    statusFilter: filtroStatus,
    pageSize: 100,
  });

  const stats = useMemo(() => {
    const total = rows.length;
    const ativos = rows.filter((row) => Number(row?.ativo) === 1).length;
    return {
      total,
      ativos,
      inativos: total - ativos,
    };
  }, [rows]);

  const rowsOrdenadas = useMemo(
    () => [...rows].sort((a, b) => formatOrigem(a).localeCompare(formatOrigem(b), "pt-BR", { sensitivity: "base" })),
    [rows]
  );

  const handleTableScroll = useCallback(
    (event) => {
      const el = event.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= 120) {
        loadMore();
      }
    },
    [loadMore]
  );

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el || loading || loadingMore || !hasMore) return;
    if (el.scrollHeight <= el.clientHeight + 12) {
      loadMore();
    }
  }, [rows.length, loading, loadingMore, hasMore, loadMore]);

  async function criarOrigem({ cidade, uf }) {
    const cidadeValue = sanitizeCidade(cidade);
    const ufValue = sanitizeUf(uf);
    if (!cidadeValue) throw new Error("Informe a cidade.");
    if (!ufValue) throw new Error("Informe a UF com 2 letras.");
    await cadastrarOrigemCarregamentoTrilhosNovos({ cidade: cidadeValue, uf: ufValue, ativo: 1 });
    setFeedback("Origem cadastrada com sucesso.");
    reload();
  }

  async function editarOrigem({ cidade, uf }) {
    if (!rowSelecionada?.id) throw new Error("Origem selecionada invalida.");
    const cidadeValue = sanitizeCidade(cidade);
    const ufValue = sanitizeUf(uf);
    if (!cidadeValue) throw new Error("Informe a cidade.");
    if (!ufValue) throw new Error("Informe a UF com 2 letras.");
    await editarOrigemCarregamentoTrilhosNovos(rowSelecionada.id, { cidade: cidadeValue, uf: ufValue });
    setFeedback("Origem atualizada com sucesso.");
    reload();
  }

  async function excluirOrigem() {
    if (!rowSelecionada?.id) throw new Error("Origem selecionada invalida.");
    await excluirOrigemCarregamentoTrilhosNovos(rowSelecionada.id);
    setFeedback("Origem excluida com sucesso.");
    reload();
  }

  async function ativarOrigem() {
    if (!rowSelecionada?.id) throw new Error("Origem selecionada invalida.");
    await alterarStatusOrigemCarregamentoTrilhosNovos(rowSelecionada.id, 1);
    setFeedback("Origem ativada com sucesso.");
    reload();
  }

  async function inativarOrigem() {
    if (!rowSelecionada?.id) throw new Error("Origem selecionada invalida.");
    await alterarStatusOrigemCarregamentoTrilhosNovos(rowSelecionada.id, 0);
    setFeedback("Origem inativada com sucesso.");
    reload();
  }

  return (
    <div className="ocn-base">
      <div className="ocn-cards">
        <div className="ocn-card">
          <span>Total</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="ocn-card">
          <span>Ativos</span>
          <strong>{stats.ativos}</strong>
        </div>
        <div className="ocn-card">
          <span>Inativos</span>
          <strong>{stats.inativos}</strong>
        </div>
      </div>

      <div className="ocn-toolbar">
        <input
          type="text"
          value={filtroTexto}
          onChange={(event) => setFiltroTexto(event.target.value)}
          placeholder="Buscar origem..."
        />
        <select value={filtroStatus} onChange={(event) => setFiltroStatus(event.target.value)}>
          <option value="todos">Todos</option>
          <option value="ativos">Somente ativos</option>
          <option value="inativos">Somente inativos</option>
        </select>
        <button
          type="button"
          className="ocn-btn ocn-btn--primary"
          onClick={() => {
            setFeedback("");
            setOpenCriar(true);
          }}
        >
          Cadastrar origem
        </button>
      </div>

      <div className="ocn-table-wrap">
        <table className="ocn-table ocn-table--head">
          <colgroup>
            <col style={{ width: "70%" }} />
            <col style={{ width: "30%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Origem de carregamento (novos)</th>
              <th>Controles</th>
            </tr>
          </thead>
        </table>

        <div className="ocn-table-scroll" ref={tableScrollRef} onScroll={handleTableScroll}>
          <table className="ocn-table ocn-table--body">
            <colgroup>
              <col style={{ width: "70%" }} />
              <col style={{ width: "30%" }} />
            </colgroup>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={2} className="ocn-empty">Carregando base de origens...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={2} className="ocn-empty ocn-empty--error">{error}</td>
                </tr>
              ) : !rowsOrdenadas.length ? (
                <tr>
                  <td colSpan={2} className="ocn-empty">Nenhuma origem encontrada.</td>
                </tr>
              ) : (
                rowsOrdenadas.map((row) => {
                  const ativo = Number(row?.ativo) === 1;
                  return (
                    <tr key={row.id}>
                      <td>{formatOrigem(row)}</td>
                      <td>
                        <div className="ocn-actions">
                          <ToggleSwitch
                            on={ativo}
                            title={ativo ? "Inativar origem" : "Ativar origem"}
                            onToggle={() => {
                              setRowSelecionada(row);
                              if (ativo) setOpenInativar(true);
                              else setOpenAtivar(true);
                            }}
                          />
                          <button
                            type="button"
                            className="ocn-icon-btn"
                            title="Editar origem"
                            onClick={() => {
                              setFeedback("");
                              setRowSelecionada(row);
                              setOpenEditar(true);
                            }}
                          >
                            <IconEdit />
                          </button>
                          <button
                            type="button"
                            className="ocn-icon-btn"
                            title="Excluir origem"
                            onClick={() => {
                              setRowSelecionada(row);
                              setOpenExcluir(true);
                            }}
                          >
                            <IconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
              {!loading && !error && loadingMore ? (
                <tr>
                  <td colSpan={2} className="ocn-empty">Carregando mais...</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {feedback ? <p className="ocn-feedback">{feedback}</p> : null}

      <CriarOrigemCarregamentoTrilhosNovosModal
        open={openCriar}
        onClose={() => setOpenCriar(false)}
        onSalvar={criarOrigem}
      />

      <EditarOrigemCarregamentoTrilhosNovosModal
        open={openEditar}
        registro={rowSelecionada}
        onClose={() => {
          setOpenEditar(false);
          setRowSelecionada(null);
        }}
        onSalvar={editarOrigem}
      />

      <ExcluirOrigemCarregamentoTrilhosNovosModal
        open={openExcluir}
        titulo={formatOrigem(rowSelecionada)}
        onClose={() => {
          setOpenExcluir(false);
          setRowSelecionada(null);
        }}
        onConfirmar={excluirOrigem}
      />

      <AtivarOrigemCarregamentoTrilhosNovosModal
        open={openAtivar}
        titulo={formatOrigem(rowSelecionada)}
        onClose={() => {
          setOpenAtivar(false);
          setRowSelecionada(null);
        }}
        onConfirmar={ativarOrigem}
      />

      <InativarOrigemCarregamentoTrilhosNovosModal
        open={openInativar}
        titulo={formatOrigem(rowSelecionada)}
        onClose={() => {
          setOpenInativar(false);
          setRowSelecionada(null);
        }}
        onConfirmar={inativarOrigem}
      />
    </div>
  );
}
